import { useState } from 'react';
import { creatorbase, creatorbaseQueries } from '@round/api';
import { showNotification } from 'helpers';
import { Button } from '@round/whitelabel-ui';
import { useLinkProjectShareMutation, useUnlinkProjectShareMutation } from '@round/creatorbase-helpers/api-hooks';
import ProjectSelect from 'Modules/Plans/Project/components/ProjectSelect/ProjectSelect';
import useProjectSelect, { ProjectOption } from 'Modules/Plans/Project/hooks/useProjectSelect';
import { inlineProjectSelectStyles } from '../../../../helpers';
import popoutStyles from '../../../../popout.module.css';

type Props = {
    share: creatorbase.ProjectShare;
    agencyName: string | undefined;
    currentProjectId: number;
    onBack: () => void;
    onDone: () => void;
};

const MoveStep = ({ share, agencyName, currentProjectId, onBack, onDone }: Props) => {
    const { unlink } = useUnlinkProjectShareMutation();
    const { link } = useLinkProjectShareMutation();
    const [isMoving, setIsMoving] = useState(false);
    const [selectedProject, setSelectedProject] = useState<ProjectOption | null>(null);
    const { getProps } = useProjectSelect({ initOn: 'mount', params: { owned_by: 'me' } });
    const selectProps = getProps({ onChange: (option) => setSelectedProject(option as ProjectOption | null) });

    const handleMove = async () => {
        if (!selectedProject) {
            return;
        }

        setIsMoving(true);

        try {
            try {
                await unlink(share.id);
            } catch (error) {
                showNotification(
                    creatorbaseQueries.projectShares.utils.unlink.getErrorMessage(error as Error),
                    'error'
                );
                return;
            }

            try {
                await link({ id: share.id, containerId: selectedProject.value });
                showNotification(`Project moved to ${selectedProject.label}`, 'info');
            } catch (error) {
                showNotification(
                    `${creatorbaseQueries.projectShares.utils.link.getErrorMessage(
                        error as Error
                    )} The project was unlinked and is back in the projects list.`,
                    'error'
                );
            }

            onDone();
        } finally {
            setIsMoving(false);
        }
    };

    return (
        <>
            <div className={popoutStyles.header}>
                <p className={popoutStyles.title}>Move project</p>
            </div>
            <div className={popoutStyles.body}>
                <p className={popoutStyles.copy}>
                    <span className={popoutStyles.bold}>{share.project_name}</span> from {agencyName ?? '-'} will move
                    to the project you choose.
                </p>
                <ProjectSelect
                    {...selectProps}
                    options={(selectProps.options as ProjectOption[] | undefined)?.filter(
                        (option) => option.value !== currentProjectId
                    )}
                    isDisabled={isMoving}
                    autoFocus
                    isClearable
                    menuIsOpen={!selectProps.value || !!selectProps.inputValue}
                    menuPortalTarget={null}
                    tabSelectsValue={false}
                    components={{ DropdownIndicator: null }}
                    styles={inlineProjectSelectStyles}
                    aria-label="Project to move to"
                    placeholder="Search your projects"
                />
            </div>
            <div className={popoutStyles.actions}>
                <Button appearance="ghost" disabled={isMoving} onClick={onBack}>
                    Back
                </Button>
                <Button appearance="primary" isLoading={isMoving} disabled={!selectedProject} onClick={handleMove}>
                    Move
                </Button>
            </div>
        </>
    );
};

export default MoveStep;
